import { useState } from "react";
import { RotateCcwKey } from "lucide-react";
import { CyberpassButton, CyberpassPhoneInput } from "../Input";
import CheckMarkIcon from "../../public/checkMark.svg?react";
import * as api from "../api/customer";
import Modal from "../Modal";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { selectAccessToken } from "../store/auth";
import { changeCurrent } from "../store/customer";
import { useTranslation } from "../i18n";

/**
 * @param {object} props
 * @param {() => void} props.onClick
 * @param {boolean} props.isActive
 * @returns {React.Component}
 */
export default function RestoreAccessButton({ onClick, isActive }) {
  const { t } = useTranslation();
  return (
    <CyberpassButton
      style={isActive ? "primary" : "secondary"}
      onClick={onClick}
      Icon={RotateCcwKey}
      iconSize="h-6 aspect-square"
    >
      <p className="">{t("admin.restoreAccess")}</p>
    </CyberpassButton>
  );
}

/**
 * @param {object} props
 * @param {(tab: string) => void} props.onSuccess
 * @returns {React.Component}
 */
export function RestoreAccessForm({ onSuccess }) {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("+7");
  /**
   * @type {[Customer | null, React.Dispatch<Customer | null>]}
   */
  const [customer, setCustomer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [enableEmailNotifications, setEnableEmailNotifications] =
    useState(false);

  const onConfirm = async () => {
    if (!customer) return;

    setIsLoading(true);
    try {
      const restoredCustomer = await api.restoreAccess(
        accessToken,
        phoneNumber,
        enableEmailNotifications,
      );
      toast.success(t("restore.success"));
      dispatch(changeCurrent(restoredCustomer));
      setPhoneNumber("+7");
      setCustomer(null);
      setEnableEmailNotifications(false);
      setShowModal(false);
      if (onSuccess) {
        onSuccess("recentCustomer");
      }
    } catch (err) {
      toast.error(typeof err === "string" ? err : t("restore.error"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @param {PhoneEvent} event - The change event from the input.
   */
  const handlePhoneChange = (event) => {
    const { value } = event.target;
    setPhoneNumber(value);
    setNotFound(false);

    if (event.phone.length === 12) {
      console.debug("Fetching customer by phone number");
      api
        .findByPhoneNumber(accessToken, value)
        .then((resp) => {
          if (!resp) {
            console.error("Customer is void!");
            setNotFound(true);
            return;
          }
          setCustomer(resp);
          setNotFound(false);
        })
        .catch(() => {
          setCustomer(null);
          setNotFound(true);
        });
    } else {
      setCustomer(null);
    }
  };

  return (
    <div className="">
      <div className="mb-4">
        <label className="text-c-gray-400 text-sm mb-1 block">
          {t("restore.phoneLabel")}
          {customer != null && (
            <span className="text-green-500 ml-2">• {t("restore.found")}</span>
          )}
          {notFound && (
            <span className="text-red-500 ml-2">• {t("restore.notFound")}</span>
          )}
        </label>
        <div className="max-w-48">
          <CyberpassPhoneInput
            id="phone_number"
            value={phoneNumber}
            onChange={handlePhoneChange}
          />
        </div>
      </div>

      {customer != null && (
        <>
          <table className="text-left">
            <tbody>
              <tr>
                <td className="text-c-gray-400 pr-4 py-1">
                  {t("restore.name")}
                </td>
                <td className="font-bold">
                  {customer.firstname} {customer.lastname || ""}
                </td>
              </tr>
              <tr>
                <td className="text-c-gray-400 pr-4 py-1">
                  {t("restore.email")}
                </td>
                <td className="font-bold">
                  {customer.email || t("common.notSpecified")}
                </td>
              </tr>
              <tr>
                <td className="text-c-gray-400 pr-4 py-1">
                  {t("restore.balance")}
                </td>
                <td className="font-bold text-green-500">
                  {customer.minutesBalance} {t("restore.balanceUnit")}
                </td>
              </tr>
            </tbody>
          </table>
          <div
            className="cursor-pointer py-2 px-3 mt-4 flex items-center gap-3 rounded-lg w-fit hover:bg-c-gray-500"
            onClick={() =>
              setEnableEmailNotifications(!enableEmailNotifications)
            }
          >
            {enableEmailNotifications ? (
              <div className="h-6 w-6 rounded-full text-accent flex items-center justify-center">
                <CheckMarkIcon className="h-6 w-6" />
              </div>
            ) : (
              <div className="h-6 w-6 rounded-full bg-c-gray-600" />
            )}
            <p>{t("restore.enableEmailNotifications")}</p>
          </div>
          <div className="mt-4">
            <CyberpassButton style="primary" onClick={() => setShowModal(true)}>
              {enableEmailNotifications
                ? t("restore.confirmWithNotifications")
                : t("restore.confirm")}
            </CyberpassButton>
          </div>
        </>
      )}

      <Modal
        title={t("restore.modalTitle")}
        content={
          customer && (
            <div className="space-y-4">
              <p>
                {t("restore.modalBody", {
                  email: customer.email || t("common.notSpecified"),
                })}
              </p>
              <p className="text-c-gray-400">{t("restore.modalHint")}</p>
            </div>
          )
        }
        showModal={showModal}
        setShowModal={setShowModal}
        onConfirm={onConfirm}
        confirmButtonText={
          isLoading ? t("common.loading") : t("restore.confirm")
        }
        disableConfirmBtn={isLoading}
        additionalButtons={
          <CyberpassButton
            style="secondary-light"
            onClick={() => setShowModal(false)}
          >
            {t("common.cancel")}
          </CyberpassButton>
        }
      />
    </div>
  );
}
